// Dependencies
import Vue from "vue";
import persist from "./persist";

// Local variables and methods
const initialState = () => {
  return {
    isValidSession: false,
    user: null,
    googleEmail: null,
    tosUpdateRequired: false,
    currentTosVersion: null
  };
};

// Store
export default options => ({
  namespaced: true,
  state: () => initialState(),
  getters: {
    isAuth: state => state.isValidSession
  },
  actions: {
    async registerWithPassword({ commit }, { email, password }) {
      console.log(commit);
      try {
        const response = await Vue.$http.auth.register(email, password);
        console.log("Registration successful:", response.data);
        return response.data;
      } catch (error) {
        console.error("Registration failed:", error);
        throw new Error("Registration failed. Please try again.");
      }
    },
    register({ commit }, { name, email, password }) {
      console.log(commit);
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .register(name, email, password)
          .then(message => {
            console.log("Registration successful:", message);

            // Properly resolve the promise so the frontend knows registration is complete
            resolve(message);
          })
          .catch(error => {
            console.error("Registration failed:", error);
            reject(error);
          });
      });
    },
    login({ commit }, { email, password }) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .login(email, password)
          .then(loginData => {
            commit("update", {
              key: "tosUpdateRequired",
              value: Boolean(loginData?.tos_update_required)
            });
            commit("update", {
              key: "currentTosVersion",
              value: loginData?.current_tos_version || null
            });

            console.log(loginData?.message || loginData);
            Vue.$http.auth
              .getUser()
              .then(user => {
                commit("update", { key: "user", value: user });
                persist.setUser(user);

                commit("update", { key: "isValidSession", value: true });

                if (options.onLoginSuccess) {
                  options.onLoginSuccess(user);
                }

                resolve({
                  user
                });
              })
              .catch(error => {
                reject(error);
              });
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    googleLogin({ commit }, { credential, source }) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .googleLogin(credential, source)
          .then(response => {
            const loginData = response?.body || response?.data || {};
            commit("update", {
              key: "tosUpdateRequired",
              value: Boolean(loginData.tos_update_required)
            });
            commit("update", {
              key: "currentTosVersion",
              value: loginData.current_tos_version || null
            });

            Vue.$http.auth
              .getUser()
              .then(user => {
                commit("update", { key: "user", value: user });
                persist.setUser(user);
                commit("update", { key: "isValidSession", value: true });

                if (options.onLoginSuccess) {
                  options.onLoginSuccess(user);
                }

                resolve({ user });
              })
              .catch(error => reject(error));
          })
          .catch(error => reject(error));
      });
    },
    resendActivation({ commit }, { email }) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .resendActivation(email)
          .then(response => {
            commit("update", {
              key: "activationMessage",
              value: "Activation email sent!"
            });
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    logout() {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .logout()
          .then(message => {
            console.log(message);
            persist.reset();
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    tempSession({ commit }, data) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .tempSession(data)
          .then(response => {
            console.log(response);
            Vue.$http.auth
              .getUser()
              .then(user => {
                commit("update", { key: "user", value: user });
                persist.setUser(user);

                commit("update", { key: "isValidSession", value: true });

                if (options.onLoginSuccess) {
                  options.onLoginSuccess(user);
                }

                resolve({
                  user,
                  session: response
                });
              })
              .catch(error => {
                reject(error);
              });
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    shareLinkOtpRequest(_context, data) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .shareLinkOtpRequest(data)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    shareLinkOtpVerify({ commit }, data) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .shareLinkOtpVerify(data)
          .then(() => {
            Vue.$http.auth
              .getUser()
              .then(user => {
                commit("update", { key: "user", value: user });
                persist.setUser(user);
                commit("update", { key: "isValidSession", value: true });

                if (options.onLoginSuccess) {
                  options.onLoginSuccess(user);
                }

                resolve({ user });
              })
              .catch(error => reject(error));
          })
          .catch(error => reject(error));
      });
    },
    shareLinkClaim(_context, data) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .shareLinkClaim(data)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    claimProfile({ commit }, data) {
      return new Promise((resolve, reject) => {
        Vue.$http.auth
          .claimProfile(data)
          .then(response => {
            const responseUser =
              response?.body?.user || response?.data?.user || null;
            if (responseUser) {
              commit("update", { key: "user", value: responseUser });
              persist.setUser(responseUser);
              commit("update", { key: "isValidSession", value: true });
              if (options.onLoginSuccess) {
                options.onLoginSuccess(responseUser);
              }
              resolve({ user: responseUser });
              return;
            }

            Vue.$http.auth
              .getUser()
              .then(user => {
                commit("update", { key: "user", value: user });
                persist.setUser(user);
                commit("update", { key: "isValidSession", value: true });
                if (options.onLoginSuccess) {
                  options.onLoginSuccess(user);
                }
                resolve({ user });
              })
              .catch(error => reject(error));
          })
          .catch(error => reject(error));
      });
    }
  },
  mutations: {
    setGoogleEmail(state, email) {
      state.googleEmail = email;
    },
    updateUser(state, payload) {
      if (state.user) {
        state.user = { ...state.user, ...payload };
        persist.setUser(state.user);
      }
    },
    reset(state) {
      Object.keys(initialState()).forEach(key => (state[key] = null));
    },
    update(state, { key, value }) {
      if (!Object.prototype.hasOwnProperty.call(initialState(), key)) {
        console.warn(`Can not update key "${key}" of the auth store.`);
        return;
      }

      state[key] = value;
    }
  }
});
